import { cond, path, pathEq, pathOr, pick, propEq, T, toPairs } from 'ramda';
import { POST_MESSAGE_ERRORS } from '../constants';
import { closePopupAction } from 'js/components/common/PopupManager/store/actions';
import { addLightBoxPopupAction } from 'js/components/popups/LightBox';
import uuid from 'uuid-random';
import { getURLParams } from 'js/utils/url.util';
import { log } from 'log';
import { store } from 'js/store';

// region Get Params

export const convertHeaderParameters = (headerParameters) => {
    const url = pathOr('', ['Location', '0'], headerParameters);
    const method = path(['X-Redirect-Method', '0'], headerParameters);
    const contentType = pathOr('', ['X-Redirect-ContentType', '0'], headerParameters);
    const content = path(['X-Redirect-Content', '0'], headerParameters);
    const xRedirectType = path(['X-Redirect-Type', '0'], headerParameters);

    const result = {
        url,
        method,
        contentType,
        xRedirectType
    };

    if (method === 'POST') {
        return {
            ...result,
            postData: getURLParams(content)
        };
    }

    return {
        ...result,
        url: content
            ? (url.includes('?') ? '&' : '?') + content
            : url
    };
};

export const get3DsParams = (srcHeaderParams, stdHeaderParams) => {
    if (!stdHeaderParams.xRedirectType) {
        return null;
    }

    const isHiddenIframe = pathEq(['X-Redirect-Type', '0'], 'threeDSMethod', srcHeaderParams);
    const postParamName = isHiddenIframe ? 'threeDSMethodData' : 'creq';

    return {
        is3DSecure: true,
        postData: {
            [postParamName]: path(['X-Redirect-Content', '0'], srcHeaderParams)
        },
        isHiddenIframe,
        redirectType: 'iframe' // 3DS opens only in iFrame
    };
};

export const getRedirectParams = (srcHeaderParams, config) => {
    const stdHeaderParams = convertHeaderParameters(srcHeaderParams);

    return {
        ...stdHeaderParams,
        ...pick(['redirectType'], config),
        ...get3DsParams(srcHeaderParams, stdHeaderParams)
    };
};

// endregion

// region Get Window

export const getNewWindow = ({ extWindow }) => {
    const intervalId = setInterval(() => {
        if (extWindow.closed) {
            clearInterval(intervalId);
            window.postMessage(
                { error: POST_MESSAGE_ERRORS.REDIRECT_WINDOW_CLOSED },
                window.location.origin
            );
        }
    }, 200);

    return {
        ref: extWindow,
        close: () => extWindow.close()
    };
};

export const getNewIframe = ({ id }, dispatch) => {
    const iframe = document.createElement('iframe');

    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';

    return {
        ref: iframe,
        close: () => dispatch(closePopupAction(id))
    };
};

export const getCurrentWindow = () => ({
    ref: window,
    close: () => {}
});

export const getPaymentWindowRef = cond([
    [propEq('redirectType', 'popup'), getNewWindow],
    [propEq('redirectType', 'iframe'), getNewIframe],
    [T, getCurrentWindow]
]);

// endregion

// region Submit Request

export const preparePostData = (name, postData) => {
    const data = name === 'PaReq'
        ? postData
        : postData.replace(/\+/g, ' ');

    return decodeURIComponent(data);
};

export const createFormWithHeaderParams = ({ url, method, contentType, postData }) => {
    const form = document.createElement('form');
    const input = document.createElement('input');

    form.action = url;
    form.method = method;
    form.contentType = contentType;
    form.style.display = 'none';
    form.acceptCharset = 'UTF-8';

    toPairs(postData)
        .forEach(([key, value]) => {
                const clonedInput = input.cloneNode();
                clonedInput.name = decodeURIComponent(key);
                clonedInput.value = preparePostData(clonedInput.name, value);
                form.appendChild(clonedInput);
            }
        );

    return form;
};

export const getSubmitIframeRequest = ({ ref, params }) =>
    params.method === 'POST'
        ? (node) => {
            const form = createFormWithHeaderParams(params);
            node.appendChild(ref);
            ref.contentDocument.documentElement.appendChild(form);
            form.submit();
        }
        : (node) => {
            node.appendChild(ref);
            ref.src = params.url;
        };

export const submitWindowRequest = ({ ref, params }) => {
    if (params.method === 'POST') {
        const form = createFormWithHeaderParams(params);
        ref.document.documentElement.appendChild(form);
        form.submit();
    } else {
        ref.location.href = params.url;
    }
};

export const submitIframe = ({ id, ref, params }, dispatch) => {
    const submitIframeRequest = getSubmitIframeRequest({ ref, params });
    const { is3DSecure, isHiddenIframe } = params;
    const beforeClose = message => () => ref
        .contentWindow
        .parent
        .postMessage(message, window.location.origin);

    let data = {
        afterRender: (node) => {
            submitIframeRequest(node);
        },
        beforeClose: beforeClose({ error: POST_MESSAGE_ERRORS.LIGHTBOX_CLOSED })
    };

    if (is3DSecure) {
        if (isHiddenIframe) {
            data = {
                ...data,
                lifetime: 1000 * 10,
                isHiddenLightBox: isHiddenIframe,
                onLifetimeEnd: beforeClose({ error: POST_MESSAGE_ERRORS.LIGHTBOX_TIMED_OUT_10S })
            };
        } else {
            data = {
                ...data,
                lifetime: 1000 * 60 * 10,
                onLifetimeEnd: beforeClose({ error: POST_MESSAGE_ERRORS.LIGHTBOX_TIMED_OUT_10M })
            };
        }
    }

    dispatch(addLightBoxPopupAction({ id, data }));
};

// endregion

export const isIE11 = !window.ActiveXObject && 'ActiveXObject' in window;

export const performRedirect = ({ data: { headerParameters } }, config, dispatch = store.dispatch) => {
    const id = uuid();
    const { extWindow } = config;
    const { redirectType, ...params } = getRedirectParams(headerParameters, config);
    const { ref, close } = getPaymentWindowRef({ id, redirectType, extWindow }, dispatch);

    return new Promise((resolve) => {
        const handleMessage = ({ data: message, source }) => {
            const win = redirectType === 'iframe' ? ref.contentWindow : ref;
            const { error, href } = message;

            if (win !== source && !POST_MESSAGE_ERRORS[error]) {
                return;
            }

            if (isIE11 && error === POST_MESSAGE_ERRORS.REDIRECT_WINDOW_CLOSED) {
                return resolve(null);
            }

            log('redirectResponse', message);

            if (!(error || href)) {
                return;
            }

            window.removeEventListener('message', handleMessage);
            close(); // payment window

            resolve({ error, href });
        };

        window.addEventListener('message', handleMessage);

        log('redirectRequest', { ...params, redirectType });

        if (redirectType === 'iframe') {
            submitIframe({ id, ref, params }, dispatch);
        } else {
            submitWindowRequest({ ref, params });
        }
    });
};
